import React, { useRef, useState, useMemo } from 'react';
import {
  Box, Typography, Chip, Tooltip, useTheme,
  Checkbox, TableCell, TableRow, useMediaQuery,
  IconButton, Skeleton, Avatar, AvatarGroup, Badge, LinearProgress, CircularProgress
} from '@mui/material';
import { 
  Visibility, PlayArrow, Pause, Star, StarBorder, 
  Schedule, Circle, Mail, 
  Phone, Person, AutoAwesome, ReplyAll, Email, Subject, Info
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useColorMode } from 'src/theme/ThemeProvider';
import { useDispatch } from 'react-redux';
import { AppDispatch, useAppSelector } from 'src/store';
import { toggleOpenConversation, updateConversation, toggleStarredOptimistic, toggleConversationStarred, updateConversationTags } from 'src/features/account/conversations.slice';
import { selectAllChannels } from 'src/features/account/channels.slice';
import { selectAllConversations } from 'src/features/account/conversations.slice';
import { alpha } from '@mui/material/styles';
import ConversationContacts from './ConversationContacts';
import { isPriorityConversation, getPriorityBorderStyle } from '../../utils/conversationUtils';
import { useTooltip } from './ConversationTooltipManager';
import useDebouncedTooltip from 'src/hooks/useDebouncedTooltip';
import FollowUpIndicator from '../FollowUpIndicator';
import ContactMediumIcons from '../Conversations/ContactMediumIcons';
import { useContactDrawer } from '../Contacts/ContactDrawer';
import ConversationParticipants from '../Conversations/ConversationParticipants';
import ClickableContactName from '../common/ClickableContactName';
import ConversationInlineTagEditor from '../Conversations/ConversationInlineTagEditor';

// Define the Conversation type inline
interface Conversation {
  id: string;
  channel_id: string;
  subject?: string;
  status?: string;
  delivery_state?: string;
  created_at?: string;
  updated_at?: string;
  memory?: Record<string, any>;
  last_message?: {
    body?: {
      text?: string;
    }
  };
  user_messages_count?: number;
  last_user_message_received_at?: string;
  messages?: Array<{
    id: string;
    content: string;
    sender_role: string;
    sender_name: string;
    created_at: string;
    status?: string;
  }>;
  participating_entities?: Array<{
    id?: string;
    role: string;
    name?: string;
    email?: string;
    phone?: string;
    contact_id?: string;
  }>;
  starred?: boolean;
  _hasFullData?: boolean;
  message_count?: number;
  delivered_assistant_messages_since_last_user_message?: number;
  tags?: string[];
}

interface ConversationRowItemProps {
  conversation: Conversation;
  selected?: boolean;
  active?: boolean;
  onSelect?: () => void;
  onSelectCheckbox?: (event?: React.MouseEvent) => void;
  showCheckbox?: boolean;
  disableClick?: boolean;
  isLoadingFullData?: boolean;
  mobileSelectMode?: boolean;
}

const ConversationRowItem: React.FC<ConversationRowItemProps> = ({
  conversation,
  selected = false,
  active = false,
  onSelect = () => {},
  onSelectCheckbox = (event?) => {},
  showCheckbox = false,
  disableClick = false,
  isLoadingFullData = false,
  mobileSelectMode = false,
}) => {

  const statusCellRef = useRef<HTMLTableCellElement>(null);
  const [localSelectMode, setLocalSelectMode] = useState(false);
  const [isTogglingDelivery, setIsTogglingDelivery] = useState(false);
  const [optimisticDeliveryState, setOptimisticDeliveryState] = useState<string | null>(null);
  
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const { mode } = useColorMode();
  const isDarkMode = mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  
  // Get channels from Redux store
  const channels = useAppSelector(selectAllChannels);

  // Get all conversations for available tags
  const allConversations = useAppSelector(selectAllConversations);

  // Extract available tags from all conversations
  const availableTags = React.useMemo(() => {
    const tags = new Set<string>();
    allConversations.forEach(conv => {
      if (conv?.tags && Array.isArray(conv.tags)) {
        conv.tags.forEach(tag => tags.add(tag));
      }
    });
    return Array.from(tags).sort();
  }, [allConversations]);

  const { showTooltip, hideTooltip } = useTooltip();
  const { openDrawer } = useContactDrawer();

  // Check if this is a priority conversation
  const isPriority = isPriorityConversation(conversation);
  
  // Get the channel for this conversation
  const channel = channels.find(ch => ch.id === conversation.channel_id);
  
  // Use the centralized debounced tooltip hook
  const { handleMouseEnter, handleMouseLeave } = useDebouncedTooltip({
    showTooltip,
    hideTooltip,
    isMobile,
    conversationId: conversation.id
  });

  // Handle star toggle
  const handleStarToggle = async (event: React.MouseEvent<HTMLButtonElement>) => {
    // Prevent the click from bubbling up to the row
    event.stopPropagation();
    event.preventDefault();
    
    const newStarredState = !conversation.starred;
    
    // Optimistic update - immediately update UI
    dispatch(toggleStarredOptimistic({ 
      conversationId: conversation.id, 
      starred: newStarredState 
    }));
    
    try {
      // Dispatch the async action to update server
      await dispatch(toggleConversationStarred({
        conversationId: conversation.id,
        starred: newStarredState
      })).unwrap();
    } catch (error) {
      console.error('Failed to update starred status', error);
      // The rejected case in the slice will revert the optimistic update
    }
  };

  // Handle delivery state toggle
  const handleDeliveryStateToggle = async (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    
    if (isTogglingDelivery) return;
    
    const currentState = optimisticDeliveryState || conversation.delivery_state;
    const newDeliveryState = currentState === 'play' ? 'pause' : 'play';
    
    // Optimistic update
    setIsTogglingDelivery(true);
    setOptimisticDeliveryState(newDeliveryState);
    
    try {
      await dispatch(updateConversation({
        conversationId: conversation.id,
        channelId: conversation.channel_id,
        conversationData: {
          delivery_state: newDeliveryState
        }
      })).unwrap();
      
      // Success - clear optimistic state
      setOptimisticDeliveryState(null);
    } catch (error) {
      console.error('Failed to update delivery state', error);
      // Revert optimistic update on error
      setOptimisticDeliveryState(null);
    } finally {
      setIsTogglingDelivery(false);
    }
  };

  const handleRowClick = () => {
    if (disableClick) return;

    onSelect();

    // Always open the conversation when clicking a row, regardless of device type
    dispatch(toggleOpenConversation(conversation.id));
    // URL navigation is handled by the reducer
  };

  const formatStatusText = (status: string) => {
    if (!status) return 'Unknown';
    
    if (status === 'awaiting_reply') return 'Awaiting User Reply';
    
    return status.replace(/_/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
  };

  // Add a helper function to get the most recent non-function message
  const getLatestNonFunctionMessage = (conversation: any) => {
    if (!conversation.messages || conversation.messages.length === 0) {
      return null;
    }
    
    // Sort messages with most recent first
    const sortedMessages = [...conversation.messages].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    
    // Find the first message that's not from function
    return sortedMessages.find(msg => msg.sender_role !== 'function');
  };

  // Add function to extract important memory details
  const getImportantMemoryDetails = (conversation: any) => {
    if (!conversation.memory) return [];
    
    const details = [];
    
    // Extract user interest/intent if available
    if (conversation.memory.user_intent) {
      details.push({
        label: 'Intent',
        value: conversation.memory.user_intent,
        emoji: '🎯'
      });
    }
    
    // Extract property info if available
    if (conversation.memory.property_details) {
      const price = conversation.memory.property_details.price;
      if (price) {
        details.push({
          label: 'Property',
          value: `$${Number(price).toLocaleString()}`,
          emoji: '🏠'
        });
      }
    }
    
    // Extract user location if available
    if (conversation.memory.user_details && conversation.memory.user_details.zip) {
      details.push({
        label: 'Location',
        value: conversation.memory.user_details.zip,
        emoji: '📍'
      });
    }
    
    // Extract current step if available
    if (conversation.memory.current_step) {
      details.push({
        label: 'Step',
        value: conversation.memory.current_step,
        emoji: '📋'
      });
    }
    
    return details;
  };

  const renderStatus = (conversation: any) => {
    const statusColors: Record<string, string> = {
      active: 'success',
      open: 'success',
      closed: 'error',
      pending: 'warning',
      needs_attention: 'error',
      draft: 'info',
      error: 'error',
      awaiting_reply: 'warning',
      standby: 'info',
      pending_user_reply: 'info',
      resolved: 'info'
    };

    const deliveryStateColors: Record<string, string> = {
      delivered: 'success',
      sent: 'info',
      failed: 'error',
      pending: 'warning',
      play: 'success',
      pause: 'warning',
      delivering: 'info'
    };

    const statusLabel = conversation.status || 'unknown';
    const deliveryState = optimisticDeliveryState || conversation.delivery_state || 'unknown';

    // The color for the status chip
    const statusColor = statusColors[statusLabel] || 'default';
    const deliveryColor = deliveryStateColors[deliveryState] || 'default';

    // Safe access to theme colors with fallbacks
    const getThemePaletteColor = (colorName: string) => {
      const fallbackColors = {
        success: '#4caf50',
        error: '#f44336',
        warning: '#ff9800',
        info: '#2196f3',
        default: '#757575'
      };
      
      return theme.palette[colorName]?.main || fallbackColors[colorName] || fallbackColors.default;
    };

    // Compute next follow-up time
    const computeNextFollowUp = (conv: any) => {
      // Check if next_autocomplete_scheduled_at exists and is a valid future date
      if (conv?.next_autocomplete_scheduled_at) {
        const autoCompleteDate = new Date(conv.next_autocomplete_scheduled_at);
        if (!isNaN(autoCompleteDate.getTime()) && autoCompleteDate > new Date()) {
          return conv.next_autocomplete_scheduled_at;
        }
      }
      
      // Check for pending messages with future delivery times
      const pending = conv?.messages?.filter((m: any) => {
        if (m.status !== 'pending_delivery' || !m.deliver_after) return false;
        const deliverDate = new Date(m.deliver_after);
        return !isNaN(deliverDate.getTime()) && deliverDate > new Date();
      });
      
      if (pending && pending.length > 0) {
        pending.sort((a: any, b: any) => new Date(a.deliver_after).getTime() - new Date(b.deliver_after).getTime());
        return pending[0].deliver_after;
      }
      
      return null;
    };

    const nextFollow = computeNextFollowUp(conversation);

    const showChip = ['needs_attention', 'draft', 'error'].includes(statusLabel);

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
        {showChip && (
          <Chip
            label={formatStatusText(statusLabel)}
            color={statusColor as any}
            size="small"
            variant="filled"
            sx={{
              height: 22,
              bgcolor: getThemePaletteColor(statusColor),
              color: 'common.white',
              '& .MuiChip-label': {
                px: 1.1,
                fontSize: '0.77rem',
                fontWeight: 600
              }
            }}
          />
        )}

        {nextFollow && (
          <Box sx={{ ml: 0.5 }}>
            <FollowUpIndicator futureTime={nextFollow} size={16} />
          </Box>
        )}
      </Box>
    );
  };

  // Get contact information
  const getContactInfo = () => {
    const contacts = conversation.participating_entities?.filter(e => 
      e.role !== 'assistant' && 
      e.role !== 'function' && 
      (e.name || e.email || e.phone)
    ) || [];
    
    const primaryContact = contacts[0];
    if (!primaryContact) return { name: 'No Contact', detail: '', count: 0 };
    
    return {
      name: primaryContact.name || primaryContact.email || primaryContact.phone || 'Contact',
      detail: primaryContact.email || primaryContact.phone || '',
      contact_id: primaryContact.contact_id,
      count: contacts.length
    };
  };

  // Get time display
  const getTimeDisplay = () => {
    if (conversation.updated_at) {
      const date = new Date(conversation.updated_at);
      const now = new Date();
      const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
      
      if (diffInHours < 1) return 'Just now';
      if (diffInHours < 24) return format(date, 'h:mm a');
      if (diffInHours < 48) return 'Yesterday';
      return format(date, 'MMM d');
    }
    return '';
  };

  const contactInfo = getContactInfo();
  const latestMessage = getLatestNonFunctionMessage(conversation);

  // Mobile card-like cell content with enhanced visuals
  const renderMobileContent = () => {
    const memoryDetails = getImportantMemoryDetails(conversation);
    const hasUnreadMessages = conversation.delivered_assistant_messages_since_last_user_message > 0;
    const messagePreviewLimit = 1;
    const previewMessages =
      conversation.messages
        ?.filter(msg => msg?.sender_role !== 'function')
        ?.slice(-messagePreviewLimit) ?? [];
    
          return (
        <Box sx={{
          p: { xs: 0, sm: 2 }, // No padding on mobile
          width: '100%',
          maxWidth: '100%',
          background: isDarkMode
            ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.7)} 100%)`
            : `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.grey[50], 0.8)} 100%)`,
          borderRadius: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': isPriority ? {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '3px',
            background: `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.error.light} 100%)`,
            animation: 'shimmer 2s ease-in-out infinite'
          } : {}
        }}>
              {conversation?.status && theme.statusGradients?.[conversation.status] && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: theme.statusGradients[conversation.status],
                    opacity: isDarkMode ? 0.15 : 0.08,
                    pointerEvents: 'none',
                    zIndex: 0,
                    ...(conversation.status === 'loading' || conversation.status === 'loading_messages' || conversation.status === 'pending_assistant_reply' ? {
                      animation: 'gradientShift 3s ease infinite',
                      '@keyframes gradientShift': {
                        '0%, 100%': { opacity: isDarkMode ? 0.15 : 0.08 },
                        '50%': { opacity: isDarkMode ? 0.25 : 0.15 },
                      },
                    } : {}),
                    ...(conversation.status === 'error' || conversation.status === 'needs_attention' ? {
                      animation: 'gradientPulse 2s ease infinite',
                      '@keyframes gradientPulse': {
                        '0%, 100%': { opacity: isDarkMode ? 0.15 : 0.08 },
                        '50%': { opacity: isDarkMode ? 0.3 : 0.2 },
                      },
                    } : {}),
                  }}
                />
              )}
        {/* Header Row with Avatar */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 0.5, sm: 1 }, mb: { xs: 0.5, sm: 1 }, px: { xs: 0.5, sm: 0 } }}>
          {/* Checkbox - only show on mobile when in select mode or item is selected */}
          {showCheckbox && (mobileSelectMode || localSelectMode || selected) && (
            <Checkbox
              size="small"
              checked={selected}
              onClick={(e) => {
                e.stopPropagation();
                onSelectCheckbox(e);
              }}
              sx={{ 
                p: 0.25,
                mt: 0.25,
                '&.Mui-checked': {
                  color: theme.palette.primary.main
                },
                animation: 'fadeIn 0.2s ease-in-out',
                '@keyframes fadeIn': {
                  '0%': { opacity: 0, transform: 'scale(0.8)' },
                  '100%': { opacity: 1, transform: 'scale(1)' }
                }
              }}
            />
          )}
          
          {/* Contact Avatar Group with participants count */}
          <ConversationParticipants
            participatingEntities={conversation.participating_entities}
            maxAvatars={2}
            avatarSize="medium"
            showOthersCount={false}
            onClick={(entity) => {
              if (entity.role === 'user' && entity.contact_id) {
                openDrawer(entity.contact_id);
              } else if (isMobile && !disableClick && showCheckbox) {
                // Fallback to checkbox selection if no contact
                setLocalSelectMode(true);
                onSelectCheckbox();
              }
            }}
          />

          {/* Contact Info and Time */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <ClickableContactName
                  variant="subtitle2"
                  contactId={conversation.participating_entities?.find(entity =>
                    entity.role === 'user'
                  )?.contact_id}
                  contactName={contactInfo.name}
                  style={{
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%'
                  }}
                />
                
                {/* Contact Details with Icons */}
                {contactInfo.detail && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                    {contactInfo.detail.includes('@') ? <Mail sx={{ fontSize: 12 }} /> : <Phone sx={{ fontSize: 12 }} />}
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{
                        fontSize: '0.75rem',
                        wordBreak: 'break-all' // Allow email/phone to break
                      }}
                    >
                      {contactInfo.detail}
                    </Typography>
                  </Box>
                )}
                
                {/* Subject line with wrapping and quick info */}
                {conversation.subject && (
                  <Box
                    sx={{
                      mt: 0.5,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 0.5,
                      width: '100%'
                    }}
                  >
                    <Subject
                      sx={{
                        fontSize: 12,
                        color: theme.palette.primary.main,
                        flexShrink: 0,
                        mt: 0.25
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.primary.main,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'normal', // Allow wrapping
                        wordBreak: 'break-word',
                        display: '-webkit-box',
                        WebkitLineClamp: 2, // Limit to 2 lines
                        WebkitBoxOrient: 'vertical',
                        flex: 1
                      }}
                    >
                      {conversation.subject}
                    </Typography>
                    {/* Quick info icon for mobile */}
                    <Tooltip 
                      title={
                        <Box sx={{ p: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>Details</Typography>
                          {conversation.tags && conversation.tags.length > 0 && (
                            <Box sx={{ mt: 0.25 }}>
                              <Typography variant="caption">Tags: {conversation.tags.join(', ')}</Typography>
                            </Box>
                          )}
                          <Box sx={{ mt: 0.25 }}>
                            <Typography variant="caption">Messages: {conversation.message_count || 0}</Typography>
                          </Box>
                        </Box>
                      }
                      arrow
                    >
                      <IconButton
                        size="small"
                        onClick={(e) => e.stopPropagation()}
                        sx={{
                          padding: '2px',
                          opacity: 0.5,
                          flexShrink: 0,
                          '&:hover': {
                            opacity: 1,
                            backgroundColor: alpha(theme.palette.primary.main, 0.08)
                          }
                        }}
                      >
                        <Info sx={{ fontSize: 12 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25, flexShrink: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  {getTimeDisplay()}
                </Typography>
                <IconButton
                  size="small"
                  onClick={handleStarToggle}
                  sx={{ 
                    p: 0.25,
                    transition: 'all 0.3s ease',
                    color: conversation.starred ? 'warning.main' : 'action.disabled',
                    '&:hover': {
                      transform: 'rotate(72deg) scale(1.2)'
                    }
                  }}
                >
                  {conversation.starred ? <Star sx={{ fontSize: 18 }} /> : <StarBorder sx={{ fontSize: 18 }} />}
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Box>


        {/* Message Previews - truncate long messages to maintain layout */}
        {previewMessages.length > 0 && (
          <Box sx={{ mb: { xs: 0.5, sm: 1 }, px: { xs: 0.5, sm: 0 }, display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: '100%' }}>
            {previewMessages.map((message, idx) => (
              <Box
                key={message.id || idx}
                sx={{
                  p: { xs: 0.5, sm: 0.75 },
                  borderRadius: 1,
                    bgcolor: message.sender_role === 'assistant' 
                      ? alpha(theme.palette.info.main, 0.08)
                      : alpha(theme.palette.success.main, 0.08),
                    border: `1px solid ${
                      message.sender_role === 'assistant'
                        ? alpha(theme.palette.info.main, 0.15)
                        : alpha(theme.palette.success.main, 0.15)
                    }`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                    {message.sender_role === 'assistant' ? (
                      <AutoAwesome sx={{ fontSize: 12, color: 'info.main' }} />
                    ) : (
                      <Person sx={{ fontSize: 12, color: 'success.main' }} />
                    )}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        fontWeight: 600, 
                        color: message.sender_role === 'assistant' ? 'info.main' : 'success.main',
                        fontSize: '0.65rem'
                      }}
                    >
                      {message.sender_role === 'assistant' ? 'AI' : message.sender_name || 'User'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontSize: '0.6rem' }}>
                      {message.created_at ? format(new Date(message.created_at), 'h:mm a') : ''}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.primary',
                      fontSize: '0.75rem',
                      lineHeight: 1.4,
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      maxWidth: '100%',
                      opacity: idx === previewMessages.length - 1 ? 1 : 0.7
                    }}
                  >
                    {message.content}
                  </Typography>
                </Box>
              ))
            }
          </Box>
        )}

        {/* Status Row - more compact on mobile */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between', 
          gap: 0.5,
          px: { xs: 0.5, sm: 0 },
          flexWrap: 'wrap' // Allow wrapping if needed
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
            {/* Status */}
            {renderStatus(conversation)}
            
            {/* Message counts with contact medium icon - only show if relevant */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {(conversation.message_count > 0 || conversation.messages?.length > 0) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <ContactMediumIcons participatingEntities={conversation.participating_entities || []} size={14} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.65rem' }}>
                    {conversation.message_count || conversation.messages?.length || 0}
                  </Typography>
                </Box>
              )}
              
              {hasUnreadMessages && (
                <Chip
                  size="small"
                  label={`${conversation.delivered_assistant_messages_since_last_user_message} new`}
                  color="error"
                  sx={{ 
                    height: 18,
                    '& .MuiChip-label': { 
                      px: 0.75, 
                      fontSize: '0.65rem',
                      fontWeight: 600 
                    }
                  }}
                />
              )}
            </Box>
          </Box>

          {/* Channel avatar - smaller on mobile */}
          {channel && (
            <Tooltip 
              title={
                isTogglingDelivery
                  ? 'Updating...'
                  : (optimisticDeliveryState || conversation.delivery_state) === 'play' 
                  ? 'Tap to pause auto-reply' 
                  : (optimisticDeliveryState || conversation.delivery_state) === 'pause'
                  ? 'Tap to enable auto-reply'
                  : 'Auto-reply status'
              } 
              arrow
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  isTogglingDelivery ? (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: 'primary.main', 
                      borderRadius: '50%', 
                      p: 0.25,
                      width: 20,
                      height: 20
                    }}>
                      <CircularProgress size={12} sx={{ color: 'white' }} />
                    </Box>
                  ) : (optimisticDeliveryState || conversation.delivery_state) === 'play' ? (
                    <PlayArrow sx={{ 
                      fontSize: 12, 
                      bgcolor: 'success.main', 
                      borderRadius: '50%', 
                      p: 0.25, 
                      color: 'white',
                      animation: optimisticDeliveryState ? 'pulse 0.8s ease-in-out infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.8, transform: 'scale(0.95)' },
                        '100%': { opacity: 1, transform: 'scale(1)' }
                      }
                    }} />
                  ) : (optimisticDeliveryState || conversation.delivery_state) === 'pause' ? (
                    <Pause sx={{ 
                      fontSize: 12, 
                      bgcolor: 'warning.main', 
                      borderRadius: '50%', 
                      p: 0.25, 
                      color: 'white',
                      animation: optimisticDeliveryState ? 'pulse 0.8s ease-in-out infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.8, transform: 'scale(0.95)' },
                        '100%': { opacity: 1, transform: 'scale(1)' }
                      }
                    }} />
                  ) : null
                }
                onClick={handleDeliveryStateToggle}
                sx={{ 
                  cursor: isTogglingDelivery ? 'wait' : 'pointer',
                  '&:active': {
                    transform: isTogglingDelivery ? 'none' : 'scale(0.95)'
                  }
                }}
              >
                <Avatar 
                  src={channel.avatar_url} 
                  alt={channel.chatbot_name}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    border: `2px solid ${theme.palette.background.paper}`,
                    boxShadow: 1,
                    transition: 'all 0.2s ease',
                    opacity: isTogglingDelivery ? 0.7 : 1,
                    '& img': {
                      objectFit: 'cover'
                    },
                    '&:hover': {
                      boxShadow: isTogglingDelivery ? 1 : 2
                    }
                  }}
                />
              </Badge>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

      return (
      <TableRow
        hover
        selected={selected}
        onClick={handleRowClick}
        sx={{ 
          cursor: disableClick ? 'default' : 'pointer',
          backgroundColor: active 
            ? alpha(theme.palette.primary.main, 0.08)
            : selected 
            ? alpha(theme.palette.primary.main, 0.04)
            : 'transparent',
          borderRadius: 0,
          overflow: 'hidden',
          mx: 0,
          my: 0,
          transition: 'none',
          // Make participant avatars monochrome and simple within this row only
          '& .MuiAvatarGroup-root .MuiAvatar-root': {
            filter: 'grayscale(100%)',
            backgroundColor: isDarkMode ? theme.palette.grey[800] : theme.palette.grey[200],
            color: isDarkMode ? theme.palette.grey[100] : theme.palette.grey[800],
            boxShadow: 'none',
            border: `1px solid ${theme.palette.divider} !important`,
          },
          '& .MuiAvatarGroup-root .MuiAvatar-root:hover': {
            transform: 'none',
          },
          '&:hover': disableClick ? {} : {
            backgroundColor: selected
              ? alpha(theme.palette.primary.main, 0.06)
              : alpha(theme.palette.action.hover, 0.04)
          },
          '& td': {
            borderBottom: 'none !important',
            borderTop: 'none !important',
            border: 'none !important'
          }
        }}
      >
      {/* On mobile, render everything in one cell */}
      {isMobile ? (
        <TableCell 
          sx={{ 
            p: 0,
            px: 0, // Remove all horizontal padding
            borderBottom: 'none', // Remove border for cleaner look
            '&:last-child': {
              paddingRight: 0,
              paddingLeft: 0
            },
            '&:first-child': {
              paddingLeft: 0
            }
          }}
          colSpan={10}
        >
          {renderMobileContent()}
        </TableCell>
      ) : (
        <>
      {/* Checkbox Column - FIRST (Desktop only) */}
      <TableCell 
        sx={{
          width: '48px', 
          minWidth: '48px', 
          maxWidth: '48px',
          p: '2px 4px',
          textAlign: 'center',
          display: 'table-cell'
        }}
      >
        {showCheckbox && (
          <Checkbox
            size="small"
            checked={selected}
            onClick={(e) => {
              e.stopPropagation();
              onSelectCheckbox(e);
            }}
            sx={{
              p: 0,
              color: theme.palette.action.active,
              '&.Mui-checked': {
                color: theme.palette.primary.main
              }
            }}
          />
        )}
      </TableCell>

      {/* Star/Favorite Column - SECOND (Desktop only) */}
      <TableCell 
        sx={{
          width: '48px', 
          minWidth: '48px', 
          maxWidth: '48px',
          p: '2px 4px',
          textAlign: 'center',
          display: 'table-cell'
        }}
      >
        <Tooltip title={conversation.starred ? "Unstar conversation" : "Star conversation"} arrow>
          <IconButton
            size="small"
            onClick={handleStarToggle}
            sx={{
              padding: '4px',
              color: conversation.starred ? 'warning.main' : 'action.disabled',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: alpha(theme.palette.warning.main, 0.08),
                color: 'warning.main',
                transform: 'rotate(72deg) scale(1.1)'
              }
            }}
          >
            {conversation.starred ? (
              <Star fontSize="small" />
            ) : (
              <StarBorder fontSize="small" sx={{ opacity: 0.5 }} />
            )}
          </IconButton>
        </Tooltip>
      </TableCell>

      {/* Combined Contact, Subject & Time cell - THIRD */}
      <TableCell
          ref={statusCellRef}
          sx={{
          width: '400px',
          minWidth: '400px',
          maxWidth: '400px',
          p: '8px',
          display: 'table-cell',
          overflow: 'hidden'
        }}
      >
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          width: '100%'
        }}>
          {/* Top Row: Contact info with time */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%'
          }}>
            <ConversationParticipants
              participatingEntities={conversation.participating_entities}
              maxAvatars={2}
              avatarSize="small"
              showOthersCount={false}
              onClick={(entity) => {
                if (entity.role === 'user' && entity.contact_id) {
                  openDrawer(entity.contact_id);
                }
              }}
            />

            {/* Contact details */}
            <Box sx={{
              flex: '1 1 auto',
              minWidth: 0,
              overflow: 'hidden'
            }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}>
                <ClickableContactName
                  variant="body2"
                  contactId={contactInfo.contact_id}
                  contactName={contactInfo.name}
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                    maxWidth: '200px'
                  }}
                />
                {contactInfo.count > 1 && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.7 }}>
                    +{contactInfo.count - 1}
                  </Typography>
                )}
              </Box>
              {contactInfo.detail && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.7rem',
                    opacity: 0.8,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block'
                  }}
                >
                  {contactInfo.detail}
                </Typography>
              )}
            </Box>

            {/* Time and Status aligned right */}
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              flexShrink: 0
            }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  opacity: 0.9
                }}
              >
                {getTimeDisplay()}
              </Typography>
              {renderStatus(conversation) && (
                <Box sx={{ mt: 0.25 }}>
                  {renderStatus(conversation)}
                </Box>
              )}
            </Box>
          </Box>

          {/* Bottom Row: Subject with quick info icon */}
          <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.5,
            width: '100%'
          }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                color: theme.palette.text.primary,
                fontSize: '0.8125rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'normal', // Changed to allow wrapping
                wordBreak: 'break-word',
                maxWidth: '300px',
                lineHeight: 1.3,
                display: '-webkit-box',
                WebkitLineClamp: 2, // Show up to 2 lines
                WebkitBoxOrient: 'vertical'
              }}
            >
              {conversation.subject || '(No subject)'}
            </Typography>
            {/* Quick info icon */}
            <Tooltip 
              title={
                <Box sx={{ p: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Conversation Details</Typography>
                  {conversation.tags && conversation.tags.length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption">Tags: {conversation.tags.join(', ')}</Typography>
                    </Box>
                  )}
                  {conversation.memory && Object.keys(conversation.memory).length > 0 && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption">Metadata: {Object.keys(conversation.memory).join(', ')}</Typography>
                    </Box>
                  )}
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption">Messages: {conversation.message_count || 0}</Typography>
                  </Box>
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="caption">Status: {formatStatusText(conversation.status || 'unknown')}</Typography>
                  </Box>
                </Box>
              }
              arrow
            >
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  // Just show tooltip, no action needed
                }}
                sx={{
                  padding: '2px',
                  opacity: 0.5,
                  '&:hover': {
                    opacity: 1,
                    backgroundColor: alpha(theme.palette.primary.main, 0.08)
                  }
                }}
              >
                <Info sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </TableCell>

      {/* Communication Method cell - FOURTH */}
      <TableCell 
        sx={{
          width: '80px', 
          minWidth: '80px', 
          maxWidth: '80px',
          p: '2px 4px',
          textAlign: 'center',
          display: 'table-cell'
        }}
      >
        <ContactMediumIcons participatingEntities={conversation.participating_entities || []} size={16} />
      </TableCell>

      {/* Channel cell - FIFTH */}
      <TableCell 
        sx={{
          width: '200px', 
          minWidth: '200px', 
          maxWidth: '200px',
          p: '2px 4px',
          display: 'table-cell'
        }}
      >
        {channel ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            p: 0
          }}>
            <Tooltip 
              title={
                isTogglingDelivery
                  ? 'Updating...'
                  : (optimisticDeliveryState || conversation.delivery_state) === 'play' 
                  ? 'Click to pause auto-reply' 
                  : (optimisticDeliveryState || conversation.delivery_state) === 'pause'
                  ? 'Click to enable auto-reply'
                  : 'Auto-reply status'
              } 
              arrow
            >
              <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                  isTogglingDelivery ? (
                    <Box sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      bgcolor: 'primary.main', 
                      borderRadius: '50%', 
                      p: 0.25,
                      width: 20,
                      height: 20
                    }}>
                      <CircularProgress size={12} sx={{ color: 'white' }} />
                    </Box>
                  ) : (optimisticDeliveryState || conversation.delivery_state) === 'play' ? (
                    <PlayArrow sx={{ 
                      fontSize: 12, 
                      bgcolor: 'success.main', 
                      borderRadius: '50%', 
                      p: 0.25, 
                      color: 'white',
                      animation: optimisticDeliveryState ? 'pulse 0.8s ease-in-out infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.8, transform: 'scale(0.95)' },
                        '100%': { opacity: 1, transform: 'scale(1)' }
                      }
                    }} />
                  ) : (optimisticDeliveryState || conversation.delivery_state) === 'pause' ? (
                    <Pause sx={{ 
                      fontSize: 12, 
                      bgcolor: 'warning.main', 
                      borderRadius: '50%', 
                      p: 0.25, 
                      color: 'white',
                      animation: optimisticDeliveryState ? 'pulse 0.8s ease-in-out infinite' : 'none',
                      '@keyframes pulse': {
                        '0%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.8, transform: 'scale(0.95)' },
                        '100%': { opacity: 1, transform: 'scale(1)' }
                      }
                    }} />
                  ) : null
                }
                onClick={handleDeliveryStateToggle}
                sx={{ 
                  cursor: isTogglingDelivery ? 'wait' : 'pointer',
                  '&:active': {
                    transform: isTogglingDelivery ? 'none' : 'scale(0.95)'
                  }
                }}
              >
                <Avatar 
                  src={channel.avatar_url} 
                  alt={channel.chatbot_name}
                  sx={{ 
                    width: 32, 
                    height: 32,
                    border: `2px solid ${theme.palette.background.paper}`,
                    boxShadow: 1,
                    transition: 'all 0.2s ease',
                    opacity: isTogglingDelivery ? 0.7 : 1,
                    '& img': {
                      objectFit: 'cover'
                    },
                    '&:hover': {
                      boxShadow: isTogglingDelivery ? 1 : 2
                    }
                  }}
                />
              </Badge>
            </Tooltip>
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, flex: 1 }}>
              <Typography 
                variant="body2" 
                sx={{
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {channel.chatbot_name}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  opacity: 0.8,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {channel.description}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', opacity: 0.6 }}>
            Channel not found
          </Typography>
        )}
      </TableCell>

      {/* Tags cell - SIXTH */}
      <TableCell
        sx={{
          width: '150px',
          minWidth: '150px',
          maxWidth: '150px',
          p: '2px 4px',
          display: 'table-cell'
        }}
      >
        <ConversationInlineTagEditor
          conversationId={conversation.id}
          currentTags={conversation.tags || []}
          availableGlobalTags={availableTags}
          onTagsUpdate={async (tags) => {
            await dispatch(updateConversationTags({
              conversationId: conversation.id,
              tags
            })).unwrap();
          }}
          compact
        />
      </TableCell>

      {/* Message Preview cell with modern styling - SEVENTH */}
      <TableCell sx={{ 
        width: '400px',
        minWidth: '400px',
        maxWidth: '400px',
        p: '2px 4px', 
        display: 'table-cell',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%' }}>
          {isDesktop && (
            <Tooltip title="Quick preview" arrow>
              <IconButton
                component="span"
                size="small"
                onMouseEnter={(e) => {
                  if (active) return;
                  if (statusCellRef.current) handleMouseEnter(e, statusCellRef.current);
                }}
                onMouseLeave={handleMouseLeave}
                onClick={(e) => {
                  e.stopPropagation();
                  showTooltip(e, conversation.id);
                }}
                sx={{ 
                  mt: 0.2,
                  p: 0.5,
                  minWidth: 'auto'
                }}
              >
                <Visibility fontSize="small" sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, flex: 1, minWidth: 0 }}>
            {/* Preview content with loading state */}
            {(() => {
              if (conversation._hasFullData === false && isLoadingFullData) {
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {/* Sender info skeleton */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Skeleton 
                        variant="circular" 
                        width={14} 
                        height={14}
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          flexShrink: 0
                        }} 
                      />
                      <Skeleton 
                        variant="text" 
                        width={40} 
                        height={14} 
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1)
                        }} 
                      />
                      <Skeleton 
                        variant="text" 
                        width={60} 
                        height={12} 
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          ml: 'auto'
                        }} 
                      />
                    </Box>
                    
                    {/* Message content skeleton - 2 lines */}
                    <Box sx={{ pl: 0.5 }}>
                      <Skeleton 
                        variant="text" 
                        width="90%" 
                        height={14} 
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                          mb: 0.5
                        }} 
                      />
                      <Skeleton 
                        variant="text" 
                        width="70%" 
                        height={14} 
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.05)
                        }} 
                      />
                    </Box>
                    
                    {/* Memory chips skeleton */}
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Skeleton 
                        variant="rounded" 
                        width={60} 
                        height={18} 
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          borderRadius: 2
                        }} 
                      />
                      <Skeleton 
                        variant="rounded" 
                        width={80} 
                        height={18} 
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          borderRadius: 2
                        }} 
                      />
                    </Box>
                  </Box>
                );
              }

              const latestMessage = getLatestNonFunctionMessage(conversation);
              if (latestMessage) {
                const isAssistant = latestMessage.sender_role === 'assistant';
                const roleIcon = isAssistant ? <AutoAwesome sx={{ fontSize: 14 }} /> : <Person sx={{ fontSize: 14 }} />;
                const roleColor = isAssistant ? theme.palette.info.main : theme.palette.success.main;
                const roleBgColor = isAssistant 
                  ? alpha(theme.palette.info.main, 0.1) 
                  : alpha(theme.palette.success.main, 0.1);

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {roleIcon}
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 500, color: roleColor, fontSize: '0.7rem' }}
                      >
                        {isAssistant ? 'AI' : latestMessage.sender_name || 'User'}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: theme.palette.text.secondary, 
                          fontSize: '0.65rem',
                          opacity: 0.7
                        }}
                      >
                        {latestMessage.created_at ? format(new Date(latestMessage.created_at), 'h:mm a') : ''}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        fontSize: '0.8125rem',
                        lineHeight: 1.4,
                        pl: 0.5,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap',
                        maxWidth: '100%'
                      }}
                    >
                      {latestMessage.content}
                    </Typography>
                  </Box>
                );
              }
              return (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontStyle: 'italic',
                    color: theme.palette.text.disabled,
                    opacity: 0.6
                  }}
                >
                  No messages yet
                </Typography>
              );
            })()}
            
            {/* Tags/Memory chips with modern design */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {getImportantMemoryDetails(conversation).slice(0, 3).map((detail, index) => (
                <Chip
                  key={index}
                  size="small"
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                      <Typography component="span" variant="caption" sx={{ fontSize: '0.7rem' }}>
                        {detail.emoji}
                      </Typography>
                      <Typography component="span" variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 500 }}>
                        {detail.value}
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    height: '18px',
                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                    border: 'none',
                    '& .MuiChip-label': { px: 0.5, py: 0 }
                  }}
                />
              ))}
              {getImportantMemoryDetails(conversation).length > 3 && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: theme.palette.text.secondary, 
                    fontSize: '0.65rem',
                    opacity: 0.7,
                    alignSelf: 'center'
                  }}
                >
                  +{getImportantMemoryDetails(conversation).length - 3} more
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </TableCell>

      {/* User Messages Count cell with modern badge - EIGHTH */}
      <TableCell 
        align="center" 
        sx={{ 
          width: '60px', 
          minWidth: '60px',
          maxWidth: '60px',
          p: '2px 4px',
          display: 'table-cell'
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600,
            color: conversation.user_messages_count > 0 
              ? theme.palette.primary.main
              : theme.palette.text.secondary,
            fontSize: '0.875rem'
          }}
        >
          {conversation.user_messages_count || 0}
        </Typography>
      </TableCell>

      {/* Last User Message cell with time indicator - NINTH */}
      <TableCell 
        align="right" 
        sx={{ 
          width: '120px', 
          minWidth: '120px',
          maxWidth: '120px',
          p: '2px 4px',
          pr: 1,
          display: 'table-cell'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'flex-end',
          gap: 0.5
        }}>

          <Typography 
            variant="caption" 
            sx={{ 
              color: theme.palette.text.secondary,
              whiteSpace: 'nowrap',
              fontWeight: 500
            }}
          >
            {conversation.last_user_message_received_at
              ? format(new Date(conversation.last_user_message_received_at), 'MMM d, h:mm a')
              : 'Never'}
          </Typography>
        </Box>
      </TableCell>

      {/* Updated At cell with activity indicator - TENTH */}
      <TableCell 
        align="right" 
        sx={{ 
          width: '100px', 
          minWidth: '100px',
          maxWidth: '100px',
          p: '2px 4px',
          pr: 2,
          display: 'table-cell'
        }}
      >
        <Typography 
          variant="caption" 
          sx={{ 
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            fontSize: '0.75rem'
          }}
        >
          {conversation.updated_at
            ? format(new Date(conversation.updated_at), 'MMM d, h:mm a')
            : 'Unknown'}
        </Typography>
      </TableCell>
        </>
      )}
    </TableRow>
  );
};

export default ConversationRowItem; 
